import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useContext, useEffect, useState } from 'react';
import { Alert, Badge, Container, Image } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';
import { LinkContainer } from 'react-router-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { LoginInfo } from '../App';
import ApiFetch from './ApiFetch';
import PasskeyService from '../service/PasskeyService';
import CampaignMessage from './CampaignMessage';
import Category from './Category';
import Dialog from './Dialog';
import FavoriteIcon from './FavoriteIcon';
import Footer from './Footer';
import Header from './Header';
import { useTranslation } from "react-i18next";
import { Button, Chip, CircularProgress, Divider } from '@mui/material';
import { 
    Stack,
    Typography,
    Box 
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';

const LoginSelect = () => {
    async function getLoginOAuth(type){
        return await new ApiFetch().get(ApiFetch.API_PROTOCOL+"//"+ApiFetch.API_HOST+"/v1/login/oauth/"+type).then((r)=>r.json().then((json)=>{
            return json
        }))
    }
    const {t,i18n} = useTranslation()
    const location = useLocation()
	const [info,setInfo] = useState("")
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false);
    
    // ログイン情報とパスキー認証用の状態
    const { loginInfo, setLoginInfo } = useContext(LoginInfo)
    const [passkeyLoading, setPasskeyLoading] = useState(false)
    const [passkeyAvailable, setPasskeyAvailable] = useState(false)

    // 共通のボタンスタイルを定義
    // このスタイルは全てのボタンで共有され、一貫性のあるデザインを提供します。
    const buttonSx = {
        width: '100%',
        height: 48,
        backgroundColor: 'white',
        border: '1px solid',
        borderColor: 'grey.300',
        color: 'text.primary',
        textTransform: 'none',
        fontSize: '16px',
        '&:hover': {
            backgroundColor: 'grey.50',
            borderColor: 'grey.400'
        },
        '&.Mui-disabled': {
            backgroundColor: 'grey.100',
            borderColor: 'grey.300',
            color: 'text.disabled'
        }
    };

    // ボタン内部のレイアウトを制御するスタイル
    const buttonContentSx = {
        width: '100%', 
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        px: 3
    };

    // アイコンの配置を制御するスタイル
    const iconContainerSx = {
        position: 'absolute',
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24
    };

    // テキストの配置とスタイルを制御
    const buttonTextSx = {
        width: '100%',
        textAlign: 'center',
        fontWeight: 500
    };

	useEffect(() => {
		document.querySelector("title").textContent = t("login.loginMethod") + " - アワートAI"
        if(location.search.indexOf("autherror=1") !== -1){
            setInfo("認証に失敗しました。")
        } else if(location.search.indexOf("autherror=2") !== -1){
            setInfo(<><p>アカウントの登録がありません。<br /><Button variant='contained' onClick={() => navigate("/regist-select")}>登録する</Button></p></>)
        } else if(location.search.indexOf("autherror=3") !== -1){
            setInfo(<><p>アカウント認証が中断されました。</p></>)
        } else if(location.search.indexOf("autherror=4") !== -1){
                setInfo(<><p>World IDはOrbによる認証が完了していないため、ログインに使用できません。</p></>)
        } else if(location.search.indexOf("autherror=5") !== -1){
            setInfo(<><p>World IDが連携されていません。</p></>)
        } else if(location.search.indexOf("autherror=6") !== -1){
            setInfo(<><p>Misskeyが連携されていません。</p></>)
        }
        
        // パスキーが利用可能かチェック
        const checkPasskeyAvailability = async () => {
            try {
                const hasPasskey = await PasskeyService.hasPasskeyAvailable()
                setPasskeyAvailable(hasPasskey)
            } catch (error) {
                console.error('パスキー確認エラー:', error)
                setPasskeyAvailable(false)
            }
        }
        checkPasskeyAvailability()
	},[location.pathname,i18n.language])
	
	// パスキーでログイン
    const handlePasskeyLogin = async () => {
        setPasskeyLoading(true)
        try {
            const result = await PasskeyService.authenticate()
            if (result.result) {
                setLoginInfo(result.data)
                window.location.href = "/"
            } else {
                alert(result.message || 'パスキー認証に失敗しました')
            }
        } catch (error) {
            console.error('パスキーログインエラー:', error)
            alert('パスキー認証に失敗しました')
        } finally {
            setPasskeyLoading(false)
        }
    }

    return(
    <div className="wrapper">
        <header className="App-header"></header>
        <Header />
        <Category />
        <Container className='text-center' style={{}}>
            <h3>{t("login.loginMethod")}</h3>
            <Alert variant='danger' style={{display:info !== "" ? "block" : "none"}}>{info}</Alert>
             <Stack spacing={2} sx={{ width: '100%', maxWidth: 'sm', mx: 'auto' }}>
            {/* Googleログインボタン */}
            <Button
                variant="contained"
                disabled={loading}
                onClick={() => {
                    setLoading(true);
                    getLoginOAuth("google").then(r => {
                        if(r.result) {
                            window.location.href = r.data.url;
                        } else {
                            navigate("/login-select?autherror=1");
                            setLoading(false);
                        }
                    }).catch(() => {
                        setLoading(false);
                    });
                }}
                sx={buttonSx}
            >
                <Box sx={buttonContentSx}>
                    <Box sx={iconContainerSx}>
                        <Icon.Google />
                    </Box>
                    <Typography sx={buttonTextSx}>
                        {t("login.loginWithGoogle")}
                    </Typography>
                </Box>
            </Button>

            {/* X (Twitter)ログインボタン */}
            <Button
                variant="contained"
                disabled={loading}
                onClick={() => {
                    setLoading(true);
                    getLoginOAuth("x").then(r => {
                        if(r.result) {
                            window.location.href = r.data.url;
                        } else {
                            navigate("/login-select?autherror=1");
                            setLoading(false);
                        }
                    }).catch(() => {
                        setLoading(false);
                    });
                }}
                sx={buttonSx}
            >
                <Box sx={buttonContentSx}>
                    <Box sx={iconContainerSx}>
                        <Icon.TwitterX />
                    </Box>
                    <Typography sx={buttonTextSx}>
                        {t("login.loginWithTwitterId")}
                    </Typography>
                </Box>
            </Button>

            {/* LINEログインボタン */}
            <Button
                variant="contained"
                disabled={loading}
                onClick={() => {
                    setLoading(true);
                    getLoginOAuth("line").then(r => {
                        if(r.result) {
                            window.location.href = r.data.url;
                        } else {
                            navigate("/login-select?autherror=1");
                            setLoading(false);
                        }
                    }).catch(() => {
                        setLoading(false);
                    });
                }}
                sx={buttonSx}
            >
                <Box sx={buttonContentSx}>
                    <Box sx={iconContainerSx}>
                        <Icon.Line />
                    </Box>
                    <Typography sx={buttonTextSx}>
                        {t("login.loginWithLINE")}
                    </Typography>
                </Box>
            </Button>

            {/* OurtAIログインボタン */}
            <Link 
                to="/login" 
                style={{ 
                    textDecoration: 'none',
                    width: '100%'
                }}
            >
                <Button
                    variant="contained"
                    disabled={loading}
                    sx={buttonSx}
                >
                    <Box sx={buttonContentSx}>
                        <Typography sx={buttonTextSx}>
                            {t("login.loginWithOurtAI")}
                        </Typography>
                    </Box>
                </Button>
            </Link>

            {/* World IDログインボタン */}
            <Button
                variant="contained"
                disabled={loading}
                onClick={() => {
                    setLoading(true);
                    getLoginOAuth("worldId").then(r => {
                        if(r.result) {
                            window.location.href = r.data.url;
                        } else {
                            navigate("/login-select?autherror=1");
                            setLoading(false);
                        }
                    }).catch(() => {
                        setLoading(false);
                    });
                }}
                sx={buttonSx}
            >
                <Box sx={buttonContentSx}>
                    <Box sx={iconContainerSx}>
                        <img 
                            src="/img/logos/worldcoin-org-wld-logo.svg" 
                            alt="World ID"
                            width={24} 
                            height={24} 
                        />
                    </Box>
                    <Typography sx={buttonTextSx}>
                        World ID
                    </Typography>
                </Box>
            </Button>

            {/* Misskeyログインボタン */}
            <Button
                variant="contained"
                disabled={loading}
                onClick={() => {
                    setLoading(true);
                    getLoginOAuth("misskey").then(r => {
                        if(r.result) {
                            window.location.href = r.data.url;
                        } else {
                            navigate("/login-select?autherror=1");
                            setLoading(false);
                        }
                    }).catch(() => {
                        setLoading(false);
                    });
                }}
                sx={buttonSx}
            >
                <Box sx={buttonContentSx}>
                    <Box sx={iconContainerSx}>
                        <img 
                            src="/img/misskey.png" 
                            alt="Misskey"
                            width={24} 
                            height={24} 
                        />
                    </Box>
                    <Typography sx={buttonTextSx}>
                        Misskey
                    </Typography>
                </Box>
            </Button>
            
            {/* パスキー認証ボタン */}
            {passkeyAvailable && (
                <>
                    <Divider sx={{ my: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            または
                        </Typography>
                    </Divider>
                    <Button
                        variant="outlined"
                        disabled={passkeyLoading || loading}
                        onClick={handlePasskeyLogin}
                        startIcon={passkeyLoading ? <CircularProgress size={20} /> : <FingerprintIcon />}
                        sx={{
                            width: '100%',
                            height: 48,
                            py: 1.5,
                            borderColor: 'primary.main',
                            textTransform: 'none',
                            fontSize: '16px',
                            '&:hover': {
                                borderColor: 'primary.dark',
                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                            }
                        }}
                    >
                        <Typography sx={{ fontWeight: 500 }}>
                            パスキーでログイン
                        </Typography>
                    </Button>
                </>
            )}
        </Stack>
            <p className='mt-3'>{t("login.message")}</p>
        </Container>
        <Footer />
    </div>
    )
}

export default LoginSelect;
